export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

export type StatusKind =
  | "order"
  | "payment"
  | "approval"
  | "po"
  | "pr"
  | "voucher"
  | "transfer"
  | "npd"
  | "npd_inward"
  | "tyno"
  | "generic";

export const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  success: "bg-green-100 text-green-700 border-green-300",
  warning: "bg-amber-100 text-amber-700 border-amber-300",
  danger: "bg-red-100 text-red-700 border-red-300",
  info: "bg-blue-100 text-blue-700 border-blue-300",
  neutral: "bg-gray-100 text-gray-600 border-gray-300",
};

const ORDER_STATUS_TONES: Record<string, StatusTone> = {
  Confirmed: "success",
  Delivered: "success",
  Installed: "success",
  Initialized: "warning",
  "Want to cancel": "warning",
  "On Hold": "warning",
  "Configuration pending": "warning",
  Cancelled: "danger",
  Dispatched: "info",
  "Partial Delivered": "info",
  Refunded: "neutral",
  "Write Off": "neutral",
};

const PAYMENT_STATUS_TONES: Record<string, StatusTone> = {
  "Full Paid": "success",
  "Partial Paid": "info",
  "Pending Payment": "warning",
};

const APPROVAL_STATUS_TONES: Record<string, StatusTone> = {
  Approved: "success",
};

const LEGACY_KINDS: StatusKind[] = ["order", "payment", "approval"];

const LEGACY_MAPS: Partial<Record<StatusKind, Record<string, StatusTone>>> = {
  order: ORDER_STATUS_TONES,
  payment: PAYMENT_STATUS_TONES,
  approval: APPROVAL_STATUS_TONES,
};

// Keyword heuristic for the many procurement workflow stages. Authoritative
// maps (above) win first; this only fires for unmapped procurement statuses.
// Order matters: danger and warning (incl. negations like "unapproved") are
// checked before success so "unapprov" doesn't match the "approv" success word.
const TONE_KEYWORDS: { tone: StatusTone; words: string[] }[] = [
  {
    tone: "danger",
    words: ["reject", "cancel", "fail", "dispute", "withdraw", "decline", "error"],
  },
  {
    tone: "warning",
    words: [
      "unapprov",
      "not approv",
      "pending",
      "hold",
      "await",
      "partial",
      "draft",
      "yet to",
      "required",
    ],
  },
  {
    tone: "success",
    words: [
      "approv",
      "complet",
      "receiv",
      "deliver",
      "success",
      "paid",
      "confirm",
      "accept",
      "done",
      "closed",
      "pushed",
      "synced",
    ],
  },
  {
    tone: "info",
    words: ["transit", "dispatch", "progress", "initiat", "submitted", "sent", "process"],
  },
];

const heuristicTone = (status: string): StatusTone => {
  const s = status.toLowerCase();
  for (const { tone, words } of TONE_KEYWORDS) {
    if (words.some((w) => s.includes(w))) return tone;
  }
  return "neutral";
};

export const getStatusTone = (
  status: string | undefined,
  kind: StatusKind = "order",
): StatusTone => {
  if (!status) return "neutral";

  const map = LEGACY_MAPS[kind];
  if (map && map[status]) return map[status];

  // Preserve legacy behaviour exactly: unmapped legacy statuses stay neutral.
  if (LEGACY_KINDS.includes(kind)) return "neutral";

  return heuristicTone(status);
};
